import React, { useState, useMemo } from 'react';
import { Printer, Search, X, ArrowLeft, QrCode } from 'lucide-react';

interface Props {
  onClose: () => void;
  unitsCount?: number;
  singleRoomNumber?: string;
  residentRole?: 'ผู้อยู่อาศัย' | 'ผู้เช่า' | 'ตัวแทน' | string;
}

interface UnitPasscode {
  roomNumber: string;
  code: string;
  expiryDateThai: string;
  expiryDateEng: string;
  role?: string;
}

// Vector QR Code Component that renders crisp on screen and in print
const SilvermanQRCode: React.FC<{ value?: string; className?: string }> = ({ className = 'w-32 h-32' }) => {
  return (
    <svg
      viewBox="0 0 120 120"
      className={className}
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Background */}
      <rect width="120" height="120" fill="white" />

      {/* Top-Left Finder Pattern */}
      <rect x="10" y="10" width="30" height="30" fill="black" />
      <rect x="14" y="14" width="22" height="22" fill="white" />
      <rect x="18" y="18" width="14" height="14" fill="black" />

      {/* Top-Right Finder Pattern */}
      <rect x="80" y="10" width="30" height="30" fill="black" />
      <rect x="84" y="14" width="22" height="22" fill="white" />
      <rect x="88" y="18" width="14" height="14" fill="black" />

      {/* Bottom-Left Finder Pattern */}
      <rect x="10" y="80" width="30" height="30" fill="black" />
      <rect x="14" y="84" width="22" height="22" fill="white" />
      <rect x="18" y="88" width="14" height="14" fill="black" />

      {/* Alignment Pattern Bottom-Right */}
      <rect x="84" y="84" width="18" height="18" fill="black" />
      <rect x="88" y="88" width="10" height="10" fill="white" />
      <rect x="91" y="91" width="4" height="4" fill="black" />

      {/* Timing lines */}
      <rect x="44" y="22" width="4" height="4" fill="black" />
      <rect x="52" y="22" width="4" height="4" fill="black" />
      <rect x="60" y="22" width="4" height="4" fill="black" />
      <rect x="68" y="22" width="4" height="4" fill="black" />

      <rect x="22" y="44" width="4" height="4" fill="black" />
      <rect x="22" y="52" width="4" height="4" fill="black" />
      <rect x="22" y="60" width="4" height="4" fill="black" />
      <rect x="22" y="68" width="4" height="4" fill="black" />

      {/* Data Modules Matrix */}
      <rect x="44" y="12" width="4" height="6" fill="black" />
      <rect x="56" y="10" width="6" height="4" fill="black" />
      <rect x="68" y="14" width="4" height="4" fill="black" />

      <rect x="44" y="32" width="6" height="4" fill="black" />
      <rect x="58" y="30" width="4" height="6" fill="black" />
      <rect x="66" y="34" width="6" height="4" fill="black" />

      <rect x="12" y="48" width="4" height="6" fill="black" />
      <rect x="32" y="44" width="6" height="4" fill="black" />
      <rect x="14" y="62" width="4" height="6" fill="black" />
      <rect x="30" y="60" width="6" height="6" fill="black" />

      <rect x="44" y="44" width="6" height="6" fill="black" />
      <rect x="54" y="42" width="6" height="4" fill="black" />
      <rect x="64" y="46" width="4" height="6" fill="black" />
      <rect x="74" y="44" width="6" height="4" fill="black" />
      <rect x="86" y="46" width="6" height="4" fill="black" />
      <rect x="98" y="44" width="4" height="6" fill="black" />
      <rect x="106" y="48" width="4" height="4" fill="black" />

      <rect x="42" y="54" width="4" height="8" fill="black" />
      <rect x="50" y="52" width="6" height="4" fill="black" />
      <rect x="60" y="56" width="8" height="4" fill="black" />
      <rect x="72" y="52" width="4" height="8" fill="black" />
      <rect x="80" y="56" width="6" height="6" fill="black" />
      <rect x="92" y="54" width="6" height="4" fill="black" />
      <rect x="104" y="56" width="6" height="4" fill="black" />

      <rect x="44" y="66" width="6" height="6" fill="black" />
      <rect x="56" y="64" width="4" height="6" fill="black" />
      <rect x="66" y="66" width="6" height="4" fill="black" />
      <rect x="76" y="64" width="4" height="8" fill="black" />
      <rect x="86" y="68" width="8" height="4" fill="black" />
      <rect x="98" y="64" width="4" height="6" fill="black" />

      <rect x="44" y="78" width="4" height="6" fill="black" />
      <rect x="54" y="76" width="6" height="4" fill="black" />
      <rect x="64" y="80" width="4" height="4" fill="black" />
      <rect x="74" y="76" width="6" height="6" fill="black" />
      <rect x="96" y="76" width="6" height="4" fill="black" />
      <rect x="106" y="78" width="4" height="6" fill="black" />

      <rect x="44" y="88" width="6" height="6" fill="black" />
      <rect x="56" y="86" width="4" height="6" fill="black" />
      <rect x="66" y="90" width="8" height="4" fill="black" />
      <rect x="78" y="88" width="4" height="6" fill="black" />
      <rect x="106" y="88" width="4" height="6" fill="black" />

      <rect x="42" y="100" width="6" height="6" fill="black" />
      <rect x="52" y="98" width="6" height="4" fill="black" />
      <rect x="62" y="102" width="6" height="4" fill="black" />
      <rect x="72" y="98" width="4" height="8" fill="black" />
      <rect x="80" y="104" width="6" height="4" fill="black" />
      <rect x="94" y="100" width="8" height="6" fill="black" />
      <rect x="106" y="102" width="4" height="4" fill="black" />
    </svg>
  );
};

export const MemberPasscodePrintModal: React.FC<Props> = ({
  onClose,
  unitsCount = 100,
  singleRoomNumber,
  residentRole = 'ผู้อยู่อาศัย',
}) => {
  const [language, setLanguage] = useState<'th' | 'en'>('th');
  const [searchQuery, setSearchQuery] = useState('');

  // Passcode generator
  const allPasscodes: UnitPasscode[] = useMemo(() => {
    // If single unit mode (e.g. 1086/01)
    if (singleRoomNumber) {
      let code = 'S R V F S Z 2';
      if (singleRoomNumber === '1086/02') code = 'N U C 9 W L N';
      else if (singleRoomNumber === '1086/03') code = 'K 7 X 2 P Q V';
      else if (singleRoomNumber !== '1086/01') {
        const parts = singleRoomNumber.replace(/[^0-9]/g, '');
        const seed = parseInt(parts || '1', 10);
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
        const c1 = chars[(seed * 3) % chars.length];
        const c2 = chars[(seed * 7 + 1) % chars.length];
        const c3 = chars[(seed * 11 + 2) % chars.length];
        const c4 = chars[(seed * 13 + 3) % chars.length];
        const c5 = chars[(seed * 17 + 4) % chars.length];
        const c6 = chars[(seed * 19 + 5) % chars.length];
        const c7 = chars[(seed * 23 + 6) % chars.length];
        code = `${c1} ${c2} ${c3} ${c4} ${c5} ${c6} ${c7}`;
      }

      return [
        {
          roomNumber: singleRoomNumber,
          code: code,
          expiryDateThai: '13 เมษายน 2024 - 12:42 น.',
          expiryDateEng: '13 April 2024 - 12:42',
          role: residentRole,
        },
      ];
    }

    // All units mode (100 units)
    const codesList: UnitPasscode[] = [];
    const samplePrefixes = [
      'M B E Y K R P',
      'N U C 9 W L N',
      'K 7 X 2 P Q V',
      'W 4 R Y 8 M T',
      'H J 9 K 3 L P',
      'B 5 N M 7 Q W',
      'Z X 8 C 2 V B',
      'Q W 3 E 9 R T',
      'Y U 6 I 4 O P',
      'A S 5 D 1 F G',
      'H J 8 K 2 L Z',
      'X C 4 V 7 B N',
      'M Q 2 W 9 E R',
      'T Y 5 U 1 I O',
      'P A 7 S 3 D F',
    ];

    for (let i = 1; i <= Math.max(unitsCount, 100); i++) {
      const roomFormatted = i < 10 ? `1086/0${i}` : `1086/${i}`;
      let customCode: string;
      if (i === 1) {
        customCode = 'M B E Y K R P';
      } else if (i === 2) {
        customCode = 'N U C 9 W L N';
      } else {
        const codeIndex = (i - 1) % samplePrefixes.length;
        const baseCode = samplePrefixes[codeIndex];
        customCode =
          i > samplePrefixes.length
            ? `${baseCode.slice(0, 4)} ${String.fromCharCode(65 + (i % 26))} ${i % 10}`
            : baseCode;
      }

      codesList.push({
        roomNumber: roomFormatted,
        code: customCode,
        expiryDateThai: '13 เมษายน 2024 - 12:40 น.',
        expiryDateEng: '13 April 2024 - 12:40',
        role: 'ผู้อยู่อาศัย',
      });
    }

    return codesList;
  }, [unitsCount, singleRoomNumber, residentRole]);

  const filteredPasscodes = useMemo(() => {
    return allPasscodes.filter((p) => {
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        return p.roomNumber.toLowerCase().includes(q) || p.code.toLowerCase().includes(q);
      }
      return true;
    });
  }, [allPasscodes, searchQuery]);

  const handlePrint = () => {
    window.print();
  };

  const isSingleMode = Boolean(singleRoomNumber);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-100/95 font-prompt">
      {/* 1. Top Control Bar (Hidden on Print) */}
      <div className="no-print sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-xs px-4 sm:px-8 py-3">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-slate-100 text-slate-600 transition-colors cursor-pointer"
              title="ย้อนกลับ / ปิดหน้าต่าง"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                {isSingleMode
                  ? `พิมพ์รหัสสมัครสมาชิก - ห้อง ${singleRoomNumber} (${residentRole})`
                  : 'พิมพ์รหัสสมัครสมาชิกสำหรับลูกบ้าน'}
                <span className="bg-sky-100 text-sky-700 text-xs px-2 py-0.5 rounded-full font-medium">
                  {filteredPasscodes.length} {isSingleMode ? 'ห้อง' : 'หลัง / ห้อง'}
                </span>
              </h2>
              <p className="text-[11px] text-slate-500">
                {isSingleMode
                  ? 'การ์ดรหัสและ QR Code สำหรับลูกบ้านห้องนี้ เพื่อสมัครและเข้าใช้งานแอปพลิเคชัน Silverman'
                  : 'เอกสารการ์ดรหัสสมัครเข้าใช้งานแอปพลิเคชัน (จัดวางหน้าละ 4 ใบ สำหรับพิมพ์ลงกระดาษ A4)'}
              </p>
            </div>
          </div>

          {/* Center / Right controls matching image: PRINT button + Language radio */}
          <div className="flex items-center gap-6">
            {/* Language switch */}
            <div className="flex items-center gap-4 text-xs font-medium text-slate-700 bg-slate-100 px-3 py-1.5 rounded-lg">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="print_lang"
                  checked={language === 'th'}
                  onChange={() => setLanguage('th')}
                  className="w-3.5 h-3.5 text-sky-600 focus:ring-sky-500 cursor-pointer"
                />
                <span>ไทย</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="print_lang"
                  checked={language === 'en'}
                  onChange={() => setLanguage('en')}
                  className="w-3.5 h-3.5 text-sky-600 focus:ring-sky-500 cursor-pointer"
                />
                <span>English</span>
              </label>
            </div>

            {/* PRINT Button matching the dark blue style from image */}
            <button
              type="button"
              onClick={handlePrint}
              className="px-8 py-2 bg-[#002664] hover:bg-[#001b48] text-white text-xs font-bold tracking-wider uppercase rounded shadow-sm hover:shadow transition-all flex items-center gap-2 cursor-pointer active:scale-98"
            >
              <Printer className="w-4 h-4" /> PRINT
            </button>

            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-slate-200 text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
              title="ปิด"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Quick Search Toolbar (only in multi-unit mode) */}
        {!isSingleMode && (
          <div className="max-w-5xl mx-auto pt-2.5 flex items-center justify-between text-xs border-t border-slate-100 mt-2">
            <div className="relative w-64">
              <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="ค้นหาเลขที่ห้อง เช่น 1086/01..."
                className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:bg-white focus:outline-none focus:border-sky-500"
              />
            </div>
            <div className="text-[11px] text-slate-500">
              * ระบบพร้อมสั่งพิมพ์และจัดหน้า A4 ให้อัตโนมัติ (สามารถสั่งพิมพ์หรือบันทึกเป็น PDF ได้ทันที)
            </div>
          </div>
        )}
      </div>

      {/* 2. Printable Container / A4 Paper Sheets */}
      <div className="py-6 px-4 flex flex-col items-center">
        <div
          className={`w-full ${
            isSingleMode ? 'max-w-[500px]' : 'max-w-[850px]'
          } bg-white shadow-xl border border-slate-300 p-8 sm:p-10 print-sheet`}
        >
          {/* Grid of Passcode Cards: 1 column centered for single mode, 2 Columns for all units */}
          <div
            className={
              isSingleMode ? 'flex justify-center' : 'grid grid-cols-1 sm:grid-cols-2 gap-8'
            }
          >
            {filteredPasscodes.map((item) => (
              <div
                key={item.roomNumber}
                className={`passcode-card border-2 border-dashed border-gray-400 bg-white p-7 flex flex-col items-center justify-between text-center relative ${
                  isSingleMode ? 'w-full max-w-[420px] min-h-[560px]' : 'min-h-[460px]'
                }`}
                style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}
              >
                {/* 1. Header Logo (SILVERMAN / SMART PROPERTY SOLUTION) */}
                <div className="flex flex-col items-center mb-2">
                  <div className="text-[9px] uppercase tracking-[0.25em] text-gray-700 font-semibold mb-0.5">
                    SMART PROPERTY SOLUTION
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="font-extrabold text-xl tracking-wider text-slate-900 font-sans">
                      SILVERMAN
                    </span>
                  </div>
                </div>

                {/* 2. Room Number & Title */}
                <div className="my-1.5">
                  <h3 className="text-sm font-bold text-slate-900">
                    {language === 'th'
                      ? `ห้องหมายเลข ${item.roomNumber}`
                      : `Unit No. ${item.roomNumber}`}
                  </h3>
                  <p className="text-xs text-slate-700 mt-0.5">
                    {language === 'th'
                      ? 'รหัสสำหรับใช้ในการสมัครเข้าใช้งานของคุณ'
                      : 'Your registration passcode for application'}
                  </p>
                </div>

                {/* 3. Big Spaced Passcode */}
                <div className="my-3 py-1 w-full">
                  <span className="text-2xl font-bold font-mono tracking-[0.35em] text-slate-950 inline-block">
                    {item.code}
                  </span>
                </div>

                {/* 4. Validity & Contact */}
                <div className="text-[11px] text-slate-700 space-y-0.5 my-1.5">
                  <div>
                    {language === 'th'
                      ? `รหัสใช้งานได้ถึงวันที่ ${item.expiryDateThai}`
                      : `Valid until ${item.expiryDateEng}`}
                  </div>
                  <div className="text-slate-600">
                    {language === 'th'
                      ? 'หากพบปัญหา กรุณาติดต่อนิติบุคคล'
                      : 'If you have any issues, please contact the management office'}
                  </div>
                </div>

                {/* 5. Download App Instruction */}
                <div className="mt-2 w-full max-w-[280px]">
                  <div className="text-[11px] text-slate-700 mb-1 font-medium">
                    {language === 'th'
                      ? 'ดาวน์โหลด Application ได้ที่'
                      : 'Download Application at'}
                  </div>

                  {/* App Store Search Input Box */}
                  <div className="w-full border border-gray-400 rounded-full py-1 px-4 flex items-center justify-center gap-2 text-xs text-gray-700 bg-white">
                    <Search className="w-3.5 h-3.5 text-gray-500" />
                    <span className="font-medium">Silverman</span>
                  </div>
                </div>

                {/* 6. QR Code (matching image 2) */}
                <div className="mt-4 flex flex-col items-center">
                  <div className="p-2 bg-white border border-gray-200 rounded-lg shadow-2xs">
                    <SilvermanQRCode className="w-28 h-28 sm:w-32 sm:h-32 text-black" />
                  </div>
                  <span className="text-[10px] text-gray-400 mt-1 font-mono tracking-wider">
                    SCAN TO REGISTER
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Embedded CSS for Print Styling */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @media print {
          @page {
            size: A4 portrait;
            margin: 8mm 8mm;
          }
          body {
            background: #ffffff !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .no-print {
            display: none !important;
          }
          .print-sheet {
            box-shadow: none !important;
            border: none !important;
            padding: 0 !important;
            max-width: 100% !important;
            width: 100% !important;
            margin: 0 !important;
            background: transparent !important;
          }
          .passcode-card {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
            margin-bottom: 20px;
          }
        }
      `,
        }}
      />
    </div>
  );
};
